import { getAnalysisJobStatus } from '../services/api';
import type { AnalysisStatus } from '../types';

interface AnalysisLike {
  analysis?: string | null;
  status: AnalysisStatus;
  job_id?: string | null;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Kick off an analysis request that may finish inline or return a background
 * job, and wait for the LLM narrative. Polls the job until it's ready.
 * Returns the narrative text, or null if it fails or can't finish in time.
 */
export const resolveAnalysisText = async (
  start: () => Promise<AnalysisLike>,
  { maxWaitMs = 90_000, intervalMs = 3_000 }: { maxWaitMs?: number; intervalMs?: number } = {}
): Promise<string | null> => {
  const res = await start();
  if (res.analysis) return res.analysis;

  const jobId = res.job_id;
  const shouldPoll = Boolean(jobId && (res.status === 'queued' || res.status === 'running'));
  if (!shouldPoll || !jobId) return res.analysis ?? null;

  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    await sleep(intervalMs);
    const data = await getAnalysisJobStatus(jobId);
    if (data.analysis) return data.analysis;
    if (data.status === 'completed') return data.analysis ?? null;
    if (data.status === 'failed') return null;
  }
  return null;
};
